import './ClientAssociations.scss';
import { useEffect, useMemo } from 'react';
import { ClientAssociationGroup } from '@blisspointmedia/bpm-types/dist/ClientAssociations';
import { ModalEditWidget } from '../../Components/ModalEditWidget';
import {
  CheckIsValidFn,
  SaveChangesInput
} from '../../Components/ModalEditWidget/modal-edit-constants';
import { Page } from '../../Components/Page/Page';
import { useAppSelector } from '../../redux';
import { useSetError } from '../../redux/modals';
import { companiesSelector } from '../../redux/user';
import {
  CLIENTS_OPTION_KEY,
  ClientAssociationsMutations,
  DEFAULT_NEW_ROW,
  GroupRow,
  HEADERS,
  PAGE_TITLE,
  PAGE_TYPE
} from './client-associations-constants';
import { findDuplicateGroupName } from './client-associations-utils';
import { useClientAssociationGroups } from './useClientAssociationGroups';

export const ClientAssociations = () => {
  const { groups, isLoading, error, createGroup, updateGroup, deleteGroup } =
    useClientAssociationGroups();
  const setError = useSetError();

  useEffect(() => {
    if (error) {
      setError({
        title: "Couldn't load client association groups",
        message: `Failed to load groups: ${error.message}`,
        reportError: error
      });
    }
  }, [error, setError]);

  return (
    <Page title={PAGE_TITLE} pageType={PAGE_TYPE} isLoading={isLoading}>
      <div className='client-associations-page'>
        <ClientAssociationsWidget
          isLoading={isLoading}
          groups={groups}
          createGroup={createGroup}
          updateGroup={updateGroup}
          deleteGroup={deleteGroup}
        />
      </div>
    </Page>
  );
};

interface ClientAssociationsWidgetProps extends ClientAssociationsMutations {
  isLoading: boolean;
  groups: ClientAssociationGroup[];
}

const ClientAssociationsWidget = ({
  isLoading,
  groups,
  createGroup,
  updateGroup,
  deleteGroup
}: ClientAssociationsWidgetProps) => {
  const setError = useSetError();
  const companies = useAppSelector(companiesSelector);

  const clientOptions = useMemo(
    () =>
      companies
        .map(entry => ({ value: entry.company, label: entry.company }))
        .sort((left, right) => String(left.label).localeCompare(String(right.label))),
    [companies]
  );

  const selectorOptions = useMemo(() => ({ [CLIENTS_OPTION_KEY]: clientOptions }), [clientOptions]);

  const checkIsValid: CheckIsValidFn<GroupRow> = useMemo(
    () => (data, handleInvalidMessage) => {
      if (!data.name || data.name.trim().length === 0) {
        handleInvalidMessage?.('Name is required.');
        return false;
      }
      const clientIds = data.client_ids ?? [];
      if (clientIds.length === 0) {
        handleInvalidMessage?.('At least one client must be selected.');
        return false;
      }
      for (const clientId of clientIds) {
        const conflictGroup = findDuplicateGroupName(clientId, data.id, groups);
        if (conflictGroup) {
          handleInvalidMessage?.(
            `Client "${clientId}" is already in group "${conflictGroup}". Remove them from that group or edit this selection.`
          );
          return false;
        }
      }
      return true;
    },
    [groups]
  );

  const onSaveModal = async ({
    addedData,
    modifiedData,
    deletedData,
    cleanup
  }: SaveChangesInput<GroupRow>) => {
    try {
      for (const row of addedData) {
        await createGroup({
          name: row.name.trim(),
          description: row.description ?? '',
          client_ids: row.client_ids ?? []
        });
      }
      for (const row of modifiedData) {
        if (!row.id) continue;
        await updateGroup({
          id: row.id,
          name: row.name.trim(),
          description: row.description ?? '',
          client_ids: row.client_ids ?? []
        });
      }
      for (const row of deletedData) {
        if (!row.id) continue;
        await deleteGroup({ id: row.id });
      }
      cleanup();
    } catch (mutationError) {
      setError({
        title: 'Failed to save client association groups',
        message: mutationError.message,
        reportError: mutationError
      });
    }
  };

  return (
    <ModalEditWidget<GroupRow>
      name='Client Association Groups'
      className='client-associations-table'
      headers={HEADERS}
      initialTableData={groups}
      onSaveModal={onSaveModal}
      selectorOptions={selectorOptions}
      checkIsValid={checkIsValid}
      defaultNewRow={DEFAULT_NEW_ROW}
      addButtonText='New Group'
      modalTitle='Client Association Group'
      isLoading={isLoading}
    />
  );
};
